/**
 * Solver for the proof-of-work challenge handed out by GET
 * /api/v1/human_check/challenge (ALTCHA PoW v2).
 *
 * The server sends
 *   { parameters: { algorithm, cost, keyLength, keyPrefix, nonce, salt, data,
 *                   expiresAt },
 *     signature, maxCounter, expires }
 *
 * The browser has to find a counter for which
 *   PBKDF2(nonce_bytes ++ uint32be(counter), salt_bytes, cost, keyLength)
 * produces a key whose hex representation starts with `keyPrefix`, and post the
 * whole challenge back together with { counter, derivedKey }. Verifying costs
 * the server a single derivation.
 *
 * v2 rather than the older v1 hash puzzle: a key derivation function is much
 * harder to accelerate on a GPU, and every parameter - the expiry included - is
 * covered by the HMAC signature, so nothing in the echoed challenge can be
 * tampered with.
 *
 * No dependencies: PBKDF2 is Web Crypto, the worker is built from a Blob at
 * runtime. That keeps the module bundler agnostic - `new Worker(new URL(...))`
 * would tie it to webpack 5 and break when copied into a webpack 4 project.
 */

// Derivations to run concurrently. Each one is milliseconds of native work, so
// a small batch keeps the crypto thread busy without computing far past the
// winning counter. Any matching counter is a valid solution, so overshooting is
// harmless.
const BATCH_SIZE = 4;

const WORKER_SOURCE = `
var HEX = [];
for (var i = 0; i < 256; i++) { HEX.push(i.toString(16).padStart(2, "0")); }

function toHex(buffer) {
  var bytes = new Uint8Array(buffer);
  var out = "";
  for (var i = 0; i < bytes.length; i++) { out += HEX[bytes[i]]; }
  return out;
}

function fromHex(hex) {
  var bytes = new Uint8Array(hex.length / 2);
  for (var i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}

function makePassword(nonceBytes, counter) {
  var out = new Uint8Array(nonceBytes.length + 4);
  out.set(nonceBytes, 0);
  new DataView(out.buffer).setUint32(nonceBytes.length, counter, false);
  return out;
}

function deriveHex(job, counter) {
  return crypto.subtle
    .importKey("raw", makePassword(job.nonce, counter), "PBKDF2", false, ["deriveBits"])
    .then(function (key) {
      return crypto.subtle.deriveBits(
        { name: "PBKDF2", salt: job.salt, iterations: job.cost, hash: job.hash },
        key,
        job.keyLength * 8
      );
    })
    .then(toHex);
}

self.onmessage = function (event) {
  var data = event.data;
  var job = {
    nonce: fromHex(data.nonce),
    salt: fromHex(data.salt),
    cost: data.cost,
    hash: data.hash,
    keyLength: data.keyLength
  };
  var started = Date.now();

  function runBatch(base) {
    var counters = [];
    var pending = [];
    var upper = Math.min(base + data.batchSize, data.maxCounter + 1);

    for (var c = base; c < upper; c++) {
      counters.push(c);
      pending.push(deriveHex(job, c));
    }

    Promise.all(pending).then(function (keys) {
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].indexOf(data.keyPrefix) === 0) {
          self.postMessage({
            counter: counters[i],
            derivedKey: keys[i],
            took: Date.now() - started
          });
          return;
        }
      }
      if (upper > data.maxCounter) {
        self.postMessage({ error: "exhausted" });
        return;
      }
      runBatch(upper);
    }).catch(function (error) {
      self.postMessage({ error: String(error) });
    });
  }

  runBatch(0);
};
`;

export const isSupported = () =>
  typeof window !== 'undefined' && !!(window.crypto && window.crypto.subtle);

const HASH_FOR_ALGORITHM = {
  'PBKDF2/SHA-256': 'SHA-256',
  'PBKDF2/SHA-384': 'SHA-384',
  'PBKDF2/SHA-512': 'SHA-512',
};

const toHex = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
};

const fromHex = (hex) => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
};

const makePassword = (nonceBytes, counter) => {
  const out = new Uint8Array(nonceBytes.length + 4);
  out.set(nonceBytes, 0);
  new DataView(out.buffer).setUint32(nonceBytes.length, counter, false);
  return out;
};

// Everything the solver needs, extracted once from the signed challenge.
const jobFor = (challenge) => {
  const parameters = challenge.parameters || {};
  const hash = HASH_FOR_ALGORITHM[parameters.algorithm];
  if (!hash) throw new Error('unsupported_algorithm');

  return {
    nonce: parameters.nonce,
    salt: parameters.salt,
    cost: parameters.cost,
    hash,
    keyLength: parameters.keyLength,
    keyPrefix: parameters.keyPrefix,
    maxCounter: challenge.maxCounter || 20000,
  };
};

/**
 * Runs on the main thread. Only used when a Worker cannot be created; it
 * competes with rendering for as long as it runs.
 */
const solveInline = async (challenge, isCancelled) => {
  const job = jobFor(challenge);
  const nonceBytes = fromHex(job.nonce);
  const saltBytes = fromHex(job.salt);
  const started = Date.now();

  const derive = async (counter) => {
    const key = await window.crypto.subtle.importKey(
      'raw',
      makePassword(nonceBytes, counter),
      'PBKDF2',
      false,
      ['deriveBits']
    );
    const bits = await window.crypto.subtle.deriveBits(
      { name: 'PBKDF2', salt: saltBytes, iterations: job.cost, hash: job.hash },
      key,
      job.keyLength * 8
    );
    return toHex(bits);
  };

  for (let base = 0; base <= job.maxCounter; base += BATCH_SIZE) {
    if (isCancelled()) return null;

    const counters = [];
    const pending = [];
    const upper = Math.min(base + BATCH_SIZE, job.maxCounter + 1);
    for (let c = base; c < upper; c++) {
      counters.push(c);
      pending.push(derive(c));
    }

    const keys = await Promise.all(pending);
    for (let i = 0; i < keys.length; i++) {
      if (keys[i].indexOf(job.keyPrefix) === 0) {
        return {
          counter: counters[i],
          derivedKey: keys[i],
          took: Date.now() - started,
        };
      }
    }
  }

  throw new Error('exhausted');
};

// The challenge is echoed back untouched - the server recomputes the HMAC over
// its parameters, so anything altered here would simply fail verification.
const encodePayload = (challenge, solution) =>
  window.btoa(
    JSON.stringify({
      challenge: {
        parameters: challenge.parameters,
        signature: challenge.signature,
      },
      solution: {
        counter: solution.counter,
        derivedKey: solution.derivedKey,
      },
    })
  );

/**
 * Returns { promise, cancel }. The promise resolves with the base64 payload
 * that has to be posted alongside the form.
 */
export const solveChallenge = (challenge) => {
  let cancelled = false;
  let worker = null;
  // Held out here so cancel() can release it too - otherwise every cancelled
  // solve (unmount, or a re-armed challenge) would leak a blob URL.
  let objectUrl = null;

  const releaseWorker = () => {
    if (worker) {
      worker.terminate();
      worker = null;
    }
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }
  };

  const cancel = () => {
    cancelled = true;
    releaseWorker();
  };

  const promise = new Promise((resolve, reject) => {
    if (!isSupported()) {
      reject(new Error('unsupported'));
      return;
    }

    let job;
    try {
      job = jobFor(challenge);
    } catch (error) {
      reject(error);
      return;
    }

    const finish = (solution) =>
      resolve({
        payload: encodePayload(challenge, solution),
        took: solution.took,
      });

    try {
      const blob = new Blob([WORKER_SOURCE], { type: 'text/javascript' });
      objectUrl = URL.createObjectURL(blob);
      worker = new Worker(objectUrl);
    } catch (error) {
      // Content security policies can forbid blob: workers. Falling back to
      // the main thread keeps the form usable.
      releaseWorker();
    }

    if (!worker) {
      solveInline(challenge, () => cancelled)
        .then((solution) => {
          if (!cancelled && solution) finish(solution);
        })
        .catch(reject);
      return;
    }

    worker.onmessage = (event) => {
      releaseWorker();
      if (cancelled) return;

      if (event.data && typeof event.data.counter === 'number') {
        finish(event.data);
      } else {
        reject(new Error((event.data && event.data.error) || 'failed'));
      }
    };

    worker.onerror = (event) => {
      releaseWorker();
      if (!cancelled) reject(new Error(event.message || 'worker_failed'));
    };

    worker.postMessage({ ...job, batchSize: BATCH_SIZE });
  });

  return { promise, cancel };
};
